import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { computeQuoteTotals } from '@/lib/quoteTotals';
import { resolveQuotePdfTemplate } from '@/lib/quotePdfTemplate';
import { QuoteTotalsBreakdown } from '@/components/quotes/QuoteTotalsBreakdown';
import { formatQuantityDisplay } from '@/lib/quantity';
import { formatPaymentMethods } from '@/lib/quotePaymentInfo';
import { quoteWarrantyFromRow, warrantyDisplayText } from '@/lib/quoteWarranty';
import { quoteExtraFieldsFromRow } from '@/lib/quoteExtraFields';
import {
  FileDown,
  Loader2,
  Building2,
  Calendar,
  User,
  Phone,
  MapPin,
  FileText,
  Clock,
  Shield,
  Zap,
  Hash,
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { buildWhatsAppUrl } from '@/lib/messageTemplates';
import { downloadQuotePDF, PDFQuoteData } from '@/lib/pdfGenerator';
import type { PdfPixDynamic } from '@/lib/pdfTemplates/shared';
import { toPdfPixDynamic } from '@/lib/asaasCharge';
import { PublicQuoteActions, type PublicApproval } from '@/components/quotes/PublicQuoteActions';
import { PublicQuotePixCard } from '@/components/quotes/PublicQuotePixCard';
import { triggerPushForLatestNotification } from '@/lib/pushNotifications';
import { ImageLightbox } from '@/components/ui/ImageLightbox';

const ACTIVE_COMPANY_KEY = 'orcazap_active_company_id';

interface QuoteData {
  id: string;
  user_id: string;
  company_id: string | null;
  client_name: string | null;
  client_phone: string | null;
  client_address: string | null;
  status: string;
  document_type: string;
  validity_days: number | null;
  warranty: string | null;
  notes: string | null;
  terms_conditions: string | null;
  /** Frete e desconto de CABEÇALHO — decompõem o `total` na tela e no PDF. */
  shipping: number | null;
  discount: number | null;
  discount_type: 'value' | 'percent' | null;
  /**
   * Campos de EXIBIÇÃO (grupo 4). `payment_methods` mantém a distinção
   * `null` (legado → o PDF continua com o QR PIX) × `[]` (recusa explícita).
   */
  payment_methods: string[] | null;
  payment_terms: string | null;
  reference: string | null;
  total: number;
  created_at: string;
  viewed_at: string | null;
  title: string | null;
  /** Cabecalho do documento — a RPC devolve a linha inteira (#125/#225). */
  subtitle: string | null;
  issue_place: string | null;
  extra_fields: unknown;
  sequence_id: number | null;
  show_sequence_id: boolean;
  date: string | null;
}

interface QuoteItemData {
  id: string;
  name: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: 'value' | 'percent' | null;
  unit: string | null;
  image_url: string | null;
  total: number;
}

interface CompanyData {
  id: string;
  name: string;
  cnpj_cpf: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  logo_url: string | null;
  primary_color: string;
  pix_key?: string | null;
  pix_city?: string | null;
}

interface OwnerProfile {
  id: string;
  business_name: string | null;
  logo_url: string | null;
  is_premium: boolean;
  plan_type: string;
  email: string | null;
  full_name: string | null;
}

interface GeneralPhotoData {
  id: string;
  image_url: string;
  title: string | null;
  description: string | null;
}

interface MediaPayload {
  companyLogoUrl: string | null;
  items: Array<{ id: string; imageUrl: string | null }>;
  generalPhotos: Array<{
    id: string;
    imageUrl: string | null;
    title: string | null;
    description: string | null;
  }>;
}

export default function PublicQuotePage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [quote, setQuote] = useState<QuoteData | null>(null);
  // A cobrança Pix EM ABERTO deste orçamento, quando houver. Vem de
  // `get_public_quote` (allowlist de 4 campos) porque `asaas_charges` é
  // owner/admin e o pagador não tem sessão nenhuma.
  const [pixCharge, setPixCharge] = useState<PdfPixDynamic | null>(null);
  const [items, setItems] = useState<QuoteItemData[]>([]);
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [ownerProfile, setOwnerProfile] = useState<OwnerProfile | null>(null);
  const [generalPhotos, setGeneralPhotos] = useState<GeneralPhotoData[]>([]);
  const [signedMedia, setSignedMedia] = useState<MediaPayload | null>(null);
  const [generating, setGenerating] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<{ url: string; title?: string; description?: string }[]>([]);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0);

  // Decomposição do total, da fonte única — a mesma que o PDF usa. Calcular
  // aqui (em vez de só exibir `quote.total`) garante que a soma que o CLIENTE lê
  // seja a mesma do documento que ele baixa.
  const publicTotals = computeQuoteTotals({
    items,
    shipping: quote?.shipping,
    discount: quote?.discount,
    discountType: quote?.discount_type,
  });

  // "PIX, Dinheiro" — `null` quando não há nada a listar (inclui o caso legado,
  // em que a coluna nunca foi preenchida).
  const acceptedMethodsLabel = formatPaymentMethods(quote?.payment_methods);

  // Determine if owner is PRO (no ads)
  const isOwnerPro = ownerProfile?.is_premium || ownerProfile?.plan_type === 'pro' || ownerProfile?.plan_type === 'admin';

  // Banner is displayed only when viewer is NOT logged in and quote owner is not PRO
  const showBanner = !user && !isOwnerPro;

  // Action buttons are displayed to non-owner viewers (clients)
  const showActions = !!quote && (!user || user.id !== quote.user_id);

  // Open lightbox for item photos
  const openItemPhotosLightbox = (clickedIndex: number) => {
    const itemsWithPhotos = items.filter(item => item.image_url);
    const images = itemsWithPhotos.map(item => ({
      url: item.image_url!,
      title: item.name,
    }));
    setLightboxImages(images);
    setLightboxInitialIndex(clickedIndex);
    setLightboxOpen(true);
  };

  // Open lightbox for general photos
  const openGeneralPhotosLightbox = (clickedIndex: number) => {
    const images = generalPhotos.map(photo => ({
      url: photo.image_url,
      title: photo.title || undefined,
      description: photo.description || undefined,
    }));
    setLightboxImages(images);
    setLightboxInitialIndex(clickedIndex);
    setLightboxOpen(true);
  };

  const fetchPublicQuote = useCallback(async (trackView = true) => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      // CRITICAL: Get current user directly from Supabase to ensure accurate check
      // This avoids race conditions where useAuth's user might be stale/null during initial load
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      // Leitura pública por FUNÇÃO, não por política aberta.
      //
      // Antes, `quotes`/`quote_items`/`quote_photos`/`companies`/`profiles`
      // tinham política `SELECT USING (true)` para `{public}` — o que permitia a
      // QUALQUER pessoa com a anon key (que está no bundle) ENUMERAR todas essas
      // tabelas, e não apenas ler o orçamento cujo link recebeu. Ver as
      // migrations 20260731121415_public_quote_function.sql (cria a RPC) e
      // 20260731130334_drop_public_read_policies.sql (derruba as políticas).
      //
      // `get_public_quote` é `security definer` e EXIGE o id: preserva o
      // compartilhamento por link e elimina a enumeração. Ela já devolve
      // orçamento + itens + fotos + empresa + dono num round-trip.
      const { data: publicPayload, error: quoteError } = await supabase.rpc('get_public_quote', {
        quote_uuid: id,
      });
      const quoteData = (publicPayload as any)?.quote ?? null;

      if (quoteError || !quoteData) {
        console.error('Quote not found:', quoteError);
        setNotFound(true);
        setLoading(false);
        return;
      }

      setQuote(quoteData as unknown as QuoteData);

      // Mesmo ponto de conversão do app logado (`toPdfPixDynamic`): repetir o
      // mapeamento aqui deixaria um dos lados sem `status`, e o guard que recusa
      // cobrança já paga passaria a receber `undefined` — o QR sumiria em
      // silêncio. Os nomes vêm em snake_case porque são os da coluna.
      const rawCharge = (publicPayload as any)?.pix_charge ?? null;
      setPixCharge(
        toPdfPixDynamic(
          rawCharge
            ? {
                pix_payload: rawCharge.payload,
                pix_qr_base64: rawCharge.qr_base64,
                value: rawCharge.value,
                status: rawCharge.status,
              }
            : null,
        ),
      );

      // GOLDEN RULE: Check if current viewer is the quote owner BEFORE any tracking
      const isOwnerViewing = currentUser && currentUser.id === quoteData.user_id;
      
      if (isOwnerViewing) {
        console.log('Visualização do dono ignorada - não rastrear nem notificar.');
      }

      // Fetch quote items, general photos, and signed media URLs in parallel.
      // The edge function is the only source of image URLs the anonymous viewer
      // can actually load once the underlying Storage buckets are private.
      // A edge function continua sendo a ÚNICA origem de URL de imagem que o
      // visitante anônimo consegue carregar (os buckets são privados).
      const mediaRes = await supabase.functions.invoke<MediaPayload>('get-public-quote-media', {
        body: { quote_id: id },
      });

      const itemsData = ((publicPayload as any)?.items ?? []) as any[];
      const photosData = ((publicPayload as any)?.photos ?? []) as any[];
      const mediaData = mediaRes.data ?? null;

      if (mediaRes.error) {
        console.warn('Failed to load signed media URLs:', mediaRes.error);
      }

      const itemImageById = new Map<string, string | null>();
      const photoImageById = new Map<string, string | null>();
      if (mediaData) {
        setSignedMedia(mediaData);
        for (const it of mediaData.items ?? []) itemImageById.set(it.id, it.imageUrl);
        for (const p of mediaData.generalPhotos ?? []) photoImageById.set(p.id, p.imageUrl);
      }

      if (itemsData) {
        const items = itemsData.map((row: any) => ({
          ...row,
          image_url: itemImageById.get(row.id) || row.image_url,
        }));
        setItems(items as QuoteItemData[]);
      }

      if (photosData) {
        const photos = photosData.map((row: any) => ({
          ...row,
          image_url: photoImageById.get(row.id) || row.image_url,
        }));
        setGeneralPhotos(photos as GeneralPhotoData[]);
      }

      // Fetch company using quote's company_id.
      // If the quote has no company_id (legacy), and the owner is viewing while logged in,
      // use the currently active company from localStorage. Otherwise, fall back to the
      // user's first company.
      const typedQuote = quoteData as unknown as QuoteData;

      // A empresa já vem resolvida pela função pública, inclusive o fallback
      // para documento legado sem `company_id` (a primeira empresa do dono).
      // Essa regra saiu do cliente e foi para o servidor de propósito: era a
      // única cópia dela, e no cliente ela dependia de leitura direta em
      // `companies`, que deixou de ser pública.
      let resolvedCompany = ((publicPayload as any)?.company ?? null) as CompanyData | null;

      // Exceção preservada: o DONO, logado e sem `company_id` no documento, vê a
      // empresa que está ativa para ele — preferência local que o servidor não
      // tem como conhecer. Ele é autenticado, então lê `companies` normalmente.
      if (!typedQuote.company_id && currentUser && currentUser.id === typedQuote.user_id) {
        const activeCompanyId = localStorage.getItem(ACTIVE_COMPANY_KEY);
        if (activeCompanyId) {
          const { data: activeCompanyData } = await supabase
            .from('companies')
            .select('*')
            .eq('id', activeCompanyId)
            .maybeSingle();

          if (activeCompanyData) {
            resolvedCompany = activeCompanyData as CompanyData;
          }
        }
      }

      if (resolvedCompany) {
        setCompany(resolvedCompany);
      }

      // Fetch owner profile for plan check and fallback company data.
      // (pix_key vive em companies.pix_key — profiles.pix_key não é consumido.)
      // Allowlist curta, montada pela função pública. Antes esta leitura ia
      // direto em `profiles`, cuja política aberta expunha stripe_*, pix_key,
      // referral_* e email de TODOS os usuários.
      const profileData = (publicPayload as any)?.owner ?? null;

      if (profileData) {
        setOwnerProfile(profileData as OwnerProfile);
      }

      // Track view ONLY if NOT the owner (Golden Rule enforced) AND trackView is true
      if (!isOwnerViewing && trackView) {
        // `viewed_at` agora é gravado DENTRO de `track_quote_view` (que é
        // `security definer`). O UPDATE direto que existia aqui dependia da
        // política `anon_can_update_viewed_at`, com USING/WITH CHECK `true` —
        // e política de RLS não restringe coluna, então ela autorizava um
        // anônimo a alterar QUALQUER campo de QUALQUER orçamento.
        // Track view and potentially create notification (rate limited to 1 per hour)
        try {
          const { data: trackingResult } = await supabase.rpc('track_quote_view', { quote_uuid: id });
          // If a notification was created, trigger push notification
          if (trackingResult === true) {
            triggerPushForLatestNotification(quoteData.user_id);
          }
        } catch (trackError) {
          // Silently fail - tracking is not critical
          console.log('View tracking not available (migration may be pending)');
        }
      }

      setLoading(false);
    } catch (e) {
      console.error('Error fetching public quote:', e);
      setNotFound(true);
      setLoading(false);
    }
  }, [id]);

  // Refetch quote data without tracking view (for status updates)
  const refetchQuote = useCallback(() => {
    fetchPublicQuote(false);
  }, [fetchPublicQuote]);

  // Assinatura recém-desenhada nesta sessão. A página roda anônima e a RLS de
  // `quote_approvals` só libera o dono, então guardamos em memória para o PDF
  // baixado logo após a aprovação já sair assinado. Recarregar a página perde
  // (degradação aceitável — o dono continua vendo a assinatura no PDF dele).
  const [justApproved, setJustApproved] = useState<PublicApproval | null>(null);

  const handleStatusChanged = useCallback((approval?: PublicApproval) => {
    if (approval) setJustApproved(approval);
    refetchQuote();
  }, [refetchQuote]);

  useEffect(() => {
    fetchPublicQuote(true);
  }, [fetchPublicQuote]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'orcamento': 'Orçamento',
      'pedido': 'Pedido de venda',
      'recibo': 'Recibo',
      'ordem_servico': 'Ordem de serviço',
      'cotacao': 'Cotação',
      'proposta': 'Proposta',
      'proposta_comercial': 'Proposta comercial',
      'estimativa': 'Estimativa',
      'plano_trabalho': 'Plano de trabalho',
      'comprovante': 'Comprovante',
    };
    return labels[type] || 'Orçamento';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700">Aprovado</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">Pendente</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-700">Concluído</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelado</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejeitado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get company data (exclusively from company table - no profile fallback for company info)
  const companyName = company?.name || 'Empresa';
  const companyLogo = signedMedia?.companyLogoUrl ?? company?.logo_url;
  const companyPhone = company?.phone;
  const companyEmail = company?.email;
  const companyAddress = company?.address;
  const companyPixKey = company?.pix_key;
  const primaryColor = company?.primary_color || '#10B981';

  /**
   * FONTE ÚNICA da garantia — a tela e o PDF desta mesma página têm que dizer a
   * mesma coisa. Antes o bloco imprimia `quote.warranty` CRU: num documento com
   * garantia ESTRUTURADA e texto livre vazio (o estado de todo orçamento salvo
   * pelo formulário novo), o cliente lia uma tela SEM garantia e baixava um PDF
   * COM garantia. A precedência é a do #104 — estruturada se houver, senão o
   * texto livre —, então nenhum documento legado muda.
   */
  const warrantyText = warrantyDisplayText(quoteWarrantyFromRow(quote));

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
    }
    return [16, 185, 129]; // Default emerald
  };

  /**
   * O payload do PDF vive num MEMO, e não dentro do handler de download, porque
   * o bloco "Pague por Pix" desta mesma página desenha a partir DELE. Com dois
   * objetos, a tela e o papel passariam a discordar sobre o valor cobrado ou
   * sobre qual chave recebe — que é exatamente a divergência tela × PDF do #135,
   * agora num lugar onde ela custa dinheiro do cliente.
   */
  const pdfData = useMemo<PDFQuoteData | null>(() => {
    if (!quote) return null;

    {
      // Map public page data to the unified PDF generator format
      // Build complete company data exclusively from company table
      // Prefer the signed logo URL so the PDF works while buckets are private.
      const companyData = company ? {
        name: company.name,
        cnpj: company.cnpj_cpf,
        address: company.address,
        phone: company.phone,
        email: company.email,
        logoUrl: signedMedia?.companyLogoUrl ?? company.logo_url ?? undefined,
        pixKey: company.pix_key || undefined,
        pixCity: company.pix_city || undefined,
      } : undefined;

      // Use the quote's date field if available, otherwise fall back to created_at
      // Extract just the date part to avoid timezone issues
      const quoteDate = (quote as any).date || quote.created_at;

      const payload: PDFQuoteData = {
        documentType: quote.document_type as PDFQuoteData['documentType'],
        // O PDF que o CLIENTE baixa tem que ser o mesmo que o prestador vê: sem
        // esta linha, a tela do link mostraria o título e o arquivo baixado dali
        // mostraria "ORÇAMENTO" — a divergência tela × papel do #135.
        title: quote.title,
        // Mesma razao da linha acima: a tela do link e o arquivo baixado
        // dali precisam mostrar o MESMO cabecalho (#135).
        subtitle: quote.subtitle,
        issuePlace: quote.issue_place,
        extraFields: quoteExtraFieldsFromRow(quote),
        client: {
          name: quote.client_name || 'Não informado',
          phone: quote.client_phone || undefined,
          address: quote.client_address || undefined,
          document: (quote as any).client_document || undefined,
        },
        company: companyData,
        quoteDate: quoteDate,
        validityDays: quote.validity_days,
        // A chamada fica REPETIDA de propósito (#75): o guard de varredura de
        // `quoteWarranty.test.ts` exige `warranty: warrantyDisplayText(` dentro
        // do payload, e trocá-la por `warrantyText` desligaria a proteção contra
        // alguém voltar a imprimir a coluna crua aqui. É a mesma expressão pura
        // sobre o mesmo `quote`, então tela e PDF não têm como divergir.
        warranty: warrantyDisplayText(quoteWarrantyFromRow(quote)) || undefined,
        notes: quote.notes,
        termsConditions: quote.terms_conditions,
        items: items.map(item => ({
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount: item.discount,
          discount_type: item.discount_type,
          unit: item.unit,
          image_url: item.image_url,
        })),
        total: quote.total,
        // Mesma decomposição que o dono vê: o PDF que o cliente baixa da página
        // pública não pode mostrar um total sem explicar frete e desconto.
        shipping: quote.shipping ?? 0,
        discount: quote.discount ?? 0,
        discountType: (quote.discount_type as 'value' | 'percent' | null) ?? 'value',
        // Campos de EXIBIÇÃO do grupo 4. `payment_methods` vai PRESERVANDO o
        // `null`: é ele que mantém o QR PIX nos documentos anteriores a esta
        // feature (ver `src/lib/quotePaymentInfo.ts`).
        paymentMethods: quote.payment_methods,
        paymentTerms: quote.payment_terms,
        reference: quote.reference,
        // A COBRANÇA é o que esta página enxerga do assunto: sem sessão, ela não
        // lê `payment_settings` nem `asaas_accounts` (RLS owner/admin). Por isso
        // a existência da cobrança é que decide, e não o interruptor — do
        // contrário o PDF do prestador e o do cliente discordariam sobre como
        // pagar. `get_public_quote` só devolve cobrança EM ABERTO, e
        // `pixQrEligible` reconfere status e valor aqui.
        pixQrMode: pixCharge ? 'dynamic' : 'static',
        pixDynamic: pixCharge,
        isPremium: isOwnerPro,
        // A pagina publica respeita a config da empresa dona do documento: o
        // cliente baixa o MESMO PDF que o prestador ve.
        logoWatermark: (company as { logo_watermark?: boolean } | null)?.logo_watermark === true,
        brandColorRGB: hexToRgb(primaryColor),
        // O escolhido no DOCUMENTO, senão o padrão da empresa: o PDF que o
        // cliente baixa pela página pública sai igual ao do prestador.
        template: resolveQuotePdfTemplate(
          (quote as { pdf_template?: string | null }).pdf_template,
          (company as { pdf_template?: string | null } | null)?.pdf_template,
        ),
        sequenceId: quote.sequence_id,
        showSequenceId: quote.show_sequence_id,
        generalPhotos: generalPhotos.map(p => ({
          imageUrl: p.image_url,
          title: p.title || undefined,
          description: p.description || undefined,
        })),
        // Só a assinatura desta sessão: nada de `quote_approvals` aqui (anon não lê).
        clientSignatureDataUrl: justApproved?.signature || undefined,
        clientSignerName: justApproved?.approverName || undefined,
        clientSignedAt: justApproved?.approvedAt || undefined,
      };

      return payload;
    }
  }, [quote, company, items, generalPhotos, signedMedia, justApproved, pixCharge, isOwnerPro, primaryColor]);

  const generatePDF = async () => {
    if (!quote || !pdfData) return;
    setGenerating(true);

    try {
      const filename = `${getDocumentTypeLabel(quote.document_type)}_${quote.id.slice(0, 8)}.pdf`;
      await downloadQuotePDF(pdfData, filename);
    } catch (e) {
      console.error('Error generating PDF:', e);
    } finally {
      setGenerating(false);
    }
  };

  const handleWhatsAppApproval = () => {
    if (!quote || !companyPhone) return;

    const message = `Olá! Aprovo o ${getDocumentTypeLabel(quote.document_type)} #${quote.id.slice(0, 8)} no valor de ${formatCurrency(quote.total)}. Podemos prosseguir?`;

    // `buildWhatsAppUrl` normaliza o telefone pelo COMPRIMENTO (o
    // `startsWith('55')` daqui lia o DDD de Santa Maria/RS como DDI, #35b) e
    // codifica a mensagem UMA única vez, sobre o texto já renderizado (#35a).
    window.open(buildWhatsAppUrl(companyPhone, message), '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando orçamento...</p>
        </div>
      </div>
    );
  }

  if (notFound || !quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center py-12 text-center">
            <FileText className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h1 className="text-xl font-semibold mb-2">Orçamento não encontrado</h1>
            <p className="text-muted-foreground">
              O link pode estar incorreto ou o orçamento foi removido.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header 
        className="sticky top-0 z-10 px-4 py-4"
        style={{ backgroundColor: primaryColor }}
      >
        {/* As QUATRO faixas desta página (cabeçalho, conteúdo, rodapé de ações
            e convite de conta) compartilham a mesma largura de propósito: elas
            se empilham por pixel e qualquer uma que não acompanhe fica
            desalinhada das outras. Alargar no desktop é o que mais rende aqui —
            é a tela que o CLIENTE abre, e ele costuma abrir no computador. */}
        <div className="max-w-2xl lg:max-w-4xl mx-auto flex items-center gap-4">
          {companyLogo ? (
            <img 
              src={companyLogo} 
              alt={companyName}
              className="h-12 w-12 rounded-xl object-cover bg-white/20"
            />
          ) : (
            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-semibold truncate">{companyName}</h1>
            {/* Mesma precedência do PDF (`resolveDocumentTitle`): o título do
                prestador substitui o rótulo do tipo. `break-words` porque aqui
                o texto é livre e cortá-lo esconderia justamente o que o título
                veio dizer (#175); sem título, o rótulo de sempre. */}
            <p className="text-white/80 text-sm break-words">
              {quote.title?.trim() || getDocumentTypeLabel(quote.document_type || 'orcamento')}
            </p>
            {/* O subtitulo acompanha o titulo NA TELA tambem: se ele saisse
                so no PDF, a pagina do cliente e o arquivo que ele baixa dali
                mostrariam cabecalhos diferentes — o defeito #135. */}
            {quote.subtitle?.trim() && (
              <p className="text-white/60 text-xs break-words">{quote.subtitle.trim()}</p>
            )}
          </div>
          {getStatusBadge(quote.status)}
        </div>
      </header>

      {/* Content */}
      {/* O respiro do fim da página soma a área segura junto com a pilha fixa —
          senão o último cartão fica embaixo dos botões no Android com gestos. */}
      <main className={`max-w-2xl lg:max-w-4xl mx-auto p-4 space-y-4 ${
        showBanner && showActions
          ? 'pb-[calc(220px+var(--safe-bottom))]'
          : (showBanner || showActions)
            ? 'pb-[calc(150px+var(--safe-bottom))]'
            : 'pb-[calc(96px+var(--safe-bottom))]'
      }`}>
        {/* Quote Info */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Cliente</p>
                <p className="font-medium">{quote.client_name || 'Não informado'}</p>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Data</p>
                <p className="font-medium">{formatDate(quote.created_at)}</p>
              </div>
            </div>

            {quote.validity_days && (
              <>
                <Separator />
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Validade</p>
                    <p className="font-medium">{quote.validity_days} dias</p>
                  </div>
                </div>
              </>
            )}

            {warrantyText && (
              <>
                <Separator />
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Garantia</p>
                    <p className="font-medium">{warrantyText}</p>
                  </div>
                </div>
              </>
            )}

            {quote.reference && (
              <>
                <Separator />
                <div className="flex items-center gap-3">
                  <Hash className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Referência</p>
                    <p className="font-medium">{quote.reference}</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Items */}
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold mb-4">Itens</h2>
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3">
                  {item.image_url && (
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      className="h-16 w-16 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{item.name}</p>
                    {item.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1 text-sm">
                      {/* Quantidade fracionária em pt-BR ("1,5x", não "1.5x"). */}
                      <span>{formatQuantityDisplay(item.quantity)}x</span>
                      <span className="text-muted-foreground">{formatCurrency(item.unit_price)}</span>
                      {item.discount > 0 && (
                        <span className="text-red-500">
                          -{item.discount_type === 'percent' ? `${item.discount}%` : formatCurrency(item.discount)}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="font-semibold text-right">{formatCurrency(item.total)}</p>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            {/*
              Mesmo componente que o prestador vê no formulário: o cliente não
              pode ler um detalhamento diferente do dono do orçamento.
            */}
            <QuoteTotalsBreakdown
              totals={publicTotals}
              formatCurrency={formatCurrency}
              className="mb-3"
            />

            {/* Total */}
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">Total</span>
              <span 
                className="text-2xl font-bold"
                style={{ color: primaryColor }}
              >
                {formatCurrency(quote.total)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Pagamento: formas aceitas + condições */}
        {(acceptedMethodsLabel || quote.payment_terms) && (
          <Card>
            <CardContent className="p-4 space-y-4">
              {acceptedMethodsLabel && (
                <div>
                  <h3 className="font-medium mb-1">Formas de pagamento aceitas</h3>
                  <p className="text-sm text-muted-foreground">{acceptedMethodsLabel}</p>
                </div>
              )}
              {quote.payment_terms && (
                <div>
                  <h3 className="font-medium mb-1">Condições de pagamento</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{quote.payment_terms}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Terms & Notes */}
        {(quote.notes || quote.terms_conditions) && (
          <Card>
            <CardContent className="p-4 space-y-4">
              {quote.notes && (
                <div>
                  <h3 className="font-medium mb-1">Observações</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{quote.notes}</p>
                </div>
              )}
              {quote.terms_conditions && (
                <div>
                  <h3 className="font-medium mb-1">Termos e condições</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{quote.terms_conditions}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Item Photos Gallery */}
        {items.some(item => item.image_url) && (
          <Card>
            <CardContent className="p-4">
              <h2 className="font-semibold mb-4">Fotos dos itens</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {items.filter(item => item.image_url).map((item, index) => (
                  <div 
                    key={item.id} 
                    className="space-y-1 cursor-pointer group"
                    onClick={() => openItemPhotosLightbox(index)}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted relative">
                      <img
                        src={item.image_url!}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </div>
                    <p className="text-xs font-semibold text-center truncate">{item.name}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* General Photos Gallery */}
        {generalPhotos.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h2 className="font-semibold mb-4">Galeria do projeto</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {generalPhotos.map((photo, index) => (
                  <div 
                    key={photo.id} 
                    className="space-y-1 cursor-pointer group"
                    onClick={() => openGeneralPhotosLightbox(index)}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted relative">
                      <img
                        src={photo.image_url}
                        alt={photo.title || 'Foto'}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </div>
                    {photo.title && (
                      <p className="text-xs font-semibold text-center truncate">{photo.title}</p>
                    )}
                    {photo.description && (
                      <p className="text-xs text-muted-foreground text-center line-clamp-2">{photo.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pague por Pix — o QR e o copia e cola saem do MESMO payload do PDF. */}
        <PublicQuotePixCard data={pdfData} />

        {/* Company Contact */}
        {(companyEmail || companyPhone || companyAddress) && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <h2 className="font-semibold">Contato</h2>
              
              {companyPhone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyPhone}</span>
                </div>
              )}
              
              {companyEmail && (
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyEmail}</span>
                </div>
              )}
              
              {companyAddress && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{companyAddress}</span>
                </div>
              )}

              {companyPixKey && (
                <div className="flex items-center gap-3">
                  <Zap className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <span className="text-xs text-muted-foreground block">Chave Pix</span>
                    <span className="text-sm font-mono">{companyPixKey}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sequence ID Footer - when enabled */}
        {quote.show_sequence_id && quote.sequence_id && (
          <div className="text-center py-4">
            <span className="text-sm text-muted-foreground font-mono">
              #{String(quote.sequence_id).padStart(4, '0')}
            </span>
          </div>
        )}
      </main>

      {/* Fixed Footer Actions */}
      {/* `pb-[var(--public-footer-pb)]` = padding de sempre + área segura. A
          classe antiga (`safe-area-inset-bottom`) não existe no projeto e não
          gerava CSS nenhum: a barra de gestos do Android cobria os botões. */}
      <footer className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 pb-[var(--public-footer-pb)]">
        <div className="max-w-2xl lg:max-w-4xl mx-auto flex gap-2">
          <Button3D
            className="flex-1"
            onClick={generatePDF}
            disabled={generating}
          >
            {generating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="mr-2 h-4 w-4" />
            )}
            Baixar PDF
          </Button3D>
          {companyPhone && (
            <Button
              variant="outline"
              className="flex-shrink-0"
              onClick={handleWhatsAppApproval}
              title="Entrar em contato via WhatsApp"
            >
              <WhatsAppIcon size={20} className="text-[#25D366]" />
            </Button>
          )}
        </div>
      </footer>

      {/* Approve/Reject Action Buttons */}
      {showActions && (
        <PublicQuoteActions
          quoteId={quote.id}
          quoteTitle={quote.title || `#${quote.sequence_id}`}
          currentStatus={quote.status}
          documentType={quote.document_type}
          quoteOwnerId={quote.user_id}
          hasBanner={showBanner}
          onStatusChanged={handleStatusChanged}
        />
      )}

      {/* Orçamigo Banner (only for anonymous non-logged users viewing non-pro quotes) */}
      {showBanner && (
        <div className="fixed bottom-[var(--public-banner-bottom)] left-0 right-0 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-4 py-3 z-40">
          <div className="max-w-2xl lg:max-w-4xl mx-auto flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20">
              <Zap className="h-4 w-4" />
            </div>
            <p className="flex-1 text-sm">
              Crie orçamentos profissionais como este com o <strong>Orçamigo</strong>
            </p>
            <Button 
              size="sm" 
              variant="secondary"
              onClick={() => window.open('https://orcamigo.com.br', '_blank')}
            >
              Criar conta
            </Button>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxInitialIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </div>
  );
}
